import React, { useRef } from "react";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import {
  usePatchProfileMutation,
  useUploadImageMutation,
  useUploadProfileImageFormDataMutation,
} from "../../redux/appSlices/collaborator/collaboratorApiSlice";
import { useSelector } from "react-redux";
import { translation } from "../../translation";
import { toast } from "react-toastify";
import { getProfilePicturePatchTarget } from "../../utils/profilePicture";

const CropProfilePic = ({
  setOpenEditPopup,
  imageSrc,
  setOpenPopup,
  setProfileLoading,
  /** When set (e.g. onboarding cover), receives cropped File; parent handles upload. Closes UI on success. */
  onCroppedUpload,
  /** Default matches MainCard (134×162). Use `1` for circular onboarding cover. */
  cropAspectRatio = 134 / 162,
}) => {
  const cropperRef = useRef();

  const collaborator = useSelector(
    (state) => state.collaborator.collaboratorInfo,
  );
  const lang = useSelector(
    (state) => state.collaborator.collaboratorInfo.language,
  );

  const [uploadProfileImageFormData] = useUploadProfileImageFormDataMutation();
  const [uploadImage] = useUploadImageMutation();
  const [patchProfile] = usePatchProfileMutation();

  const saveProfilePicture = async (file) => {
    const patchTarget = getProfilePicturePatchTarget(collaborator);

    if (patchTarget?.type === "profile") {
      const uploadResult = await uploadImage(file).unwrap();
      const profilePictureUrl = uploadResult?.data?.imagePath;

      if (!profilePictureUrl) {
        throw new Error("Missing uploaded image path");
      }

      await patchProfile({
        id: patchTarget.id,
        content: { profile_picture: profilePictureUrl },
      }).unwrap();
      return;
    }

    await uploadProfileImageFormData(file).unwrap();
  };

  const onSubmit = async () => {
    const cropper = cropperRef?.current?.cropper;
    if (!cropper) return;

    const canvas = cropper.getCroppedCanvas();
    if (!canvas) return;

    const data = canvas.toDataURL();
    const byteString = atob(data.split(",")[1]);
    const mimeString = data.split(",")[0].split(":")[1].split(";")[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    const blob = new Blob([ab], { type: mimeString });
    const file = new File([blob], "cropped_image.jpg", { type: "image/jpeg" });

    setOpenPopup?.(false);
    setOpenEditPopup(false);
    setProfileLoading?.(true);

    try {
      if (onCroppedUpload) {
        await onCroppedUpload(file);
      } else {
        await saveProfilePicture(file);
      }
    } catch {
      toast.error(translation.cropImage.errorUpload[lang]);
    } finally {
      setProfileLoading?.(false);
    }
  };

  return (
    <section className="pt-[7vh] mx-5">
      {imageSrc && (
        <>
          <div className="min-h-[75vh]">
            <p className="mb-5 pl-[4vh] text-[18px]">
              {translation.cropImage.title?.[lang]}
            </p>
            <div className="relative w-full h-[60vh]">
              <Cropper
                src={URL.createObjectURL(imageSrc)}
                ref={cropperRef}
                viewMode={1}
                aspectRatio={cropAspectRatio}
              />
            </div>
          </div>
          <div className="button-container">
            <button
              className="btn-fields-terminer btn-active"
              onClick={onSubmit}
            >
              {translation.cropImage.button?.[lang]}
            </button>
          </div>
        </>
      )}
    </section>
  );
};

export default CropProfilePic;
